/* eslint-disable */
import React, { useState, useContext, useEffect, useCallback } from 'react';
import './userManagement.scss';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { AuthContext } from '../../Contexts/AuthContext';
import MapForm from './mapRoles';
import useUsers from './CustomHooks/useUsers';
import { hasAccess, getUsersData } from './customUtils';
import SearchBar from './searchBar';
import ActionButtons from './actionButton';
import UserDataGrid from './userDataGrid';
import {
  USER_MANAGEMENT,
  USER_MANAGEMENT_FIELD,
  USER_MANAGEMENT_HEADER,
} from '../../Constants/strings';
import { useSnackbar } from '../../Components/Snackbar/snackBarProvider';
import KebabIcon from '../../Assets/images/svg/dar_kebab.svg';
import { useNavigate } from 'react-router-dom';
import { MODULE_NAMES } from '../../Constants/modules';
import ButtonComponent from '../../Components/Button/button';
import ArrowDropDown from '../../Assets/images/svg/drop-down-arrow.svg';
import UseAxios from '../../Api/http/http';
import { userDeleteMapRole } from '../../Api/request/roleMap';
import { getUserDataReportExcel } from '../../Api/request/reports';
import PuzzleLoader from '../../Assets/images/svg/loading puzzle.gif';
import Loader from '../../Components/Loader';

const Users = () => {
  const { rows, setRows } = useUsers();
  const [searchText, setSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState({});
  const [mapUsersOpen, setMapUsersOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editMapUserModalOpen, setEditMapUserModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editRoleRows, setEditRoleRows] = useState([]);
  const [isDataEdit, setIsDataEdit] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLaptopView, setIsLaptopView] = useState(false);
  const { userProfile } = useContext(AuthContext);
  const { snackbarOpen } = useSnackbar();
  const permissions = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_USER] || [];
  const navigate = useNavigate();
  const [selectedDownloads, setSelectedDownloads] = useState([]);
  const [anchorElDownload, setAnchorElDownload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  const checkMobileView = () => {
    setIsMobileView(window.innerWidth < 1024);
    setIsLaptopView(window.innerWidth >= 1024 && window.innerWidth <= 1500);
  };

  useEffect(() => {
    setLoading(true);
    getUsersData(setRows, snackbarOpen).finally(() => setLoading(false));
  }, [setRows, snackbarOpen]);

  const handleSearch = useCallback((event) => {
    setSearchText(event.target.value.toLowerCase());
  }, []);

  const handleActionClick = useCallback((event, rowId) => {
    setAnchorEl((prev) => ({ ...prev, [rowId]: event.currentTarget }));
    setIsDataEdit(true);
  }, []);

  const handleCloseMenu = useCallback((rowId) => {
    setAnchorEl((prev) => ({ ...prev, [rowId]: null }));
    setIsDataEdit(false);
  }, []);

  const handleDownloadClick = (event) => {
    setAnchorElDownload(event.currentTarget);
  };

  const handleCloseDownloadMenu = () => {
    setSelectedDownloads([]);
    setAnchorElDownload(null);
  };

  const handleToggleDownloadOption = (value) => {
    if (selectedDownloads.includes(value)) {
      setSelectedDownloads([]);
    } else {
      setSelectedDownloads([value]);
    }
  };

  const handleCancelDownloads = () => {
    setSelectedDownloads([]);
  };

  const handleEditUsersClick = useCallback((rowId) => {
    getUsersData((data) => {
      const selectedRow = data.find((row) => row.id === rowId);
      if (selectedRow) {
        const { fullName, employeeId, emailId, roleName, roleMapId } = selectedRow;
        const filteredRow = { fullName, employeeId, emailId, roleName, roleMapId };
        setEditRoleRows([filteredRow]);
        setIsEdit(true);
        setEditMapUserModalOpen(true);
        handleCloseMenu(selectedRow.id);
      }
    }, snackbarOpen);
  }, [handleCloseMenu, snackbarOpen]);

  const handleViewProfile = useCallback((row) => {
    if (row) {
      const { emailId } = row;
      if (emailId) {
        navigate('/employee-profile', { state: { emailId: emailId } });
      }
    }
  }, []);

  const handleMapUsersClick = useCallback(() => {
    setMapUsersOpen(true);
    setIsDataEdit(true);
    setTimeout(() => {
      setIsDataEdit(false);
    }, 100);
  }, []);

  const handleMapUsersClose = useCallback(() => {
    setMapUsersOpen(false);
    setIsDataEdit(true);
    setTimeout(() => {
      setIsDataEdit(false);
    }, 100);
  }, []);

  const handleEditUsersClose = useCallback(() => {
    setEditMapUserModalOpen(false);
    setIsEdit(false);
    setIsDataEdit(true);
    setTimeout(() => {
      setIsDataEdit(false);
    }, 100);
  }, []);

  const [removeModalOpen, setRemoveModalOpen] = useState(false);

  const handleRemoveRoleClick = useCallback(() => {
    setRemoveModalOpen(true);
    setIsDataEdit(true);
    setTimeout(() => {
      setIsDataEdit(false);
    }, 100);
  }, []);

  const handleRemoveRoleClose = useCallback(() => {
    setRemoveModalOpen(false);
    setIsDataEdit(true);
    setTimeout(() => {
      setIsDataEdit(false);
    }, 100);
  }, []);




  const handleRowSelectionChange = useCallback((newSelection) => {
    setIsDataEdit(true);
    const selectedRowData = rows.filter(
      (row) => newSelection.includes(row.id) && row.roleName !== 'Super Admin' && row.emailId && typeof row.emailId === 'string' && row.emailId.trim() !== '',
    );
    const filteredSelection = selectedRowData.map((item) => ({
      fullName: item.fullName,
      emailId: item.emailId.trim(),
      roleName: item.roleName,
      employeeId: item.employeeId,
      roleMapId: item.roleMapId,
    }));

    setSelectedRows(filteredSelection);
    setTimeout(() => {
      setIsDataEdit(false);
    }, 100);
  }, [rows]);

  const filteredRows = searchText.length >= 1
    ? rows.filter((row) => {
      return (
        row.employeeId.toLowerCase().includes(searchText)
        || row.fullName.toLowerCase().includes(searchText)
        || row.emailId.toLowerCase().includes(searchText)
        || row.location.toLowerCase().includes(searchText)
        || row.vertical.toLowerCase().includes(searchText)
        || row.department.toLowerCase().includes(searchText)
        || row.designation.toLowerCase().includes(searchText)
        || row.roleName.toLowerCase().includes(searchText)
      );
    })
    : rows;

  const getColumnWidth = (mobile, laptop, defaultWidth) => {
    if (isMobileView) {
      return mobile;
    } else if (isLaptopView) {
      return laptop;
    }
    return defaultWidth;
  };

  const getColumnFlex = (defaultFlex) => {
    if (!isMobileView && !isLaptopView) {
      return defaultFlex;
    }
    return undefined;
  };

  const DOWNLOAD_OPTIONS = [
    { value: 'role-based-report', label: 'Role based report' },
    { value: 'user-based-report', label: 'User based report' },
  ];

  const handleDownloadExcel = async (reportType) => {
    try {
      await getUserDataReportExcel(reportType);
      snackbarOpen(USER_MANAGEMENT.DOWNLOAD_SUCCESS, 'success');
    } catch (error) {
      snackbarOpen(USER_MANAGEMENT.DOWNLOAD_FAILURE, 'error');
    } finally {
      handleCloseDownloadMenu();
    }
  };

  const columns = [
    {
      field: USER_MANAGEMENT_FIELD.USER_ID,
      headerName: USER_MANAGEMENT_HEADER.ID,
      width: getColumnWidth(110, 170, undefined),
      flex: getColumnFlex(0.09),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: USER_MANAGEMENT_FIELD.NAME,
      headerName: USER_MANAGEMENT_HEADER.NAME,
      width: getColumnWidth(160, 220, undefined),
      flex: getColumnFlex(0.14),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: USER_MANAGEMENT_FIELD.EMAIL,
      headerName: USER_MANAGEMENT_HEADER.EMAIL,
      width: getColumnWidth(280, 308, undefined),
      flex: getColumnFlex(0.2),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: USER_MANAGEMENT_FIELD.WORK_LOCATION,
      headerName: USER_MANAGEMENT_HEADER.WORK_LOCATION,
      width: getColumnWidth(145, 180, undefined),
      flex: getColumnFlex(0.1),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: USER_MANAGEMENT_FIELD.VERTICAL,
      headerName: USER_MANAGEMENT_HEADER.VERTICAL,
      width: getColumnWidth(145, 180, undefined),
      flex: getColumnFlex(0.1),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: USER_MANAGEMENT_FIELD.DEPARTMENT,
      headerName: USER_MANAGEMENT_HEADER.DEPARTMENT,
      width: getColumnWidth(140, 190, undefined),
      flex: getColumnFlex(0.09),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: USER_MANAGEMENT_FIELD.DESIGNATION,
      headerName: USER_MANAGEMENT_HEADER.DESIGNATION,
      width: getColumnWidth(180, 200, undefined),
      flex: getColumnFlex(0.12),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: USER_MANAGEMENT_FIELD.ROLE,
      headerName: USER_MANAGEMENT_HEADER.ROLE,
      width: 150,
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const mobileColumns = [
    ...columns,
    {
      field: 'action',
      headerName: 'Actions',
      headerClassName: 'action-column',
      cellClassName: 'action-cell',
      flex: 0.08,
      minWidth: 110,
      renderCell: (params) => {
        const isMenuOpen = Boolean(anchorEl[params.row.id]);
        return (
          <div className="user-action-icons">
            <button
              aria-label="Edit"
              className="users-action-edit-icon"
              onClick={(event) => handleActionClick(event, params.row.id)}
              id="users-kebab-btn"
            >
              <img src={KebabIcon} alt="Edit" />
            </button>
            <Menu
              anchorEl={anchorEl[params.row.id]}
              open={isMenuOpen}
              onClose={() => handleCloseMenu(params.row.id)}
            >
              <MenuItem
                onClick={() => handleEditUsersClick(params.row.id)}
                className="user-actions-dropdown"
                id="edit-users-role-btn"
                disabled={!hasAccess('update', permissions) || params.row.roleName === 'NA'
                  || params.row.roleName === 'Super Admin'}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => handleViewProfile(params.row)}
                className="user-actions-dropdown"
                id="users-profile-btn"
              >
                View Profile
              </MenuItem>
            </Menu>
          </div>
        );
      },
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  return (
    <div className="users-manage-outer-layer">
      <div className="users-manage">
        <div className="users-manage-title">Users</div>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <img
              src={PuzzleLoader}
              alt="Loading..."
              className="loader-gif"
              style={{ width: '100px', height: 'auto' }}
            />
          </div>
        ) : (
          <>
            <div className="users-search-and-buttons">
              <div className="users-left-section">
                <SearchBar searchText={searchText} handleSearch={handleSearch} />
              </div>
              <div className="users-right-section">
                <div className="download-and-expand-wrapper">
                  <ButtonComponent
                    label="Download as Excel"
                    variant="text"
                    onClick={handleDownloadClick}
                    endIcon={<img alt="Arrow Down" src={ArrowDropDown} />}
                    btnCss="download-reports"
                  />

                  <Menu
                    anchorEl={anchorElDownload}
                    open={Boolean(anchorElDownload)}
                    onClose={handleCloseDownloadMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                      className: 'download-dropdown-menu',
                    }}
                  >
                    {DOWNLOAD_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        className="download-certify-dropdown"
                        onClick={() => handleDownloadExcel(option.value)}
                        sx={{
                          '&:hover': {
                            color: '#6B5CE8 !important',
                          },
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>

                <ActionButtons
                  hasAccess={(action) => hasAccess(action, permissions)}
                  selectedRows={selectedRows}
                  handleMapUsersClick={handleMapUsersClick}
                  handleRemoveRoleClick={handleRemoveRoleClick}
                />
              </div>
            </div>

            <MapForm
              modalOpen={isEdit ? editMapUserModalOpen : (removeModalOpen || mapUsersOpen)}
              handleClose={isEdit ? handleEditUsersClose : (removeModalOpen ? handleRemoveRoleClose : handleMapUsersClose)}
              setRows={setRows}
              isEditMode={isEdit}
              isRemoveMode={removeModalOpen}
              selectedData={isEdit ? editRoleRows : selectedRows}
              handleRowSelectionChange={handleRowSelectionChange}
              setActionLoading={setActionLoading}
            />
            <UserDataGrid
              filteredRows={filteredRows}
              columns={columns}
              handleRowSelectionChange={handleRowSelectionChange}
              dataColumns={mobileColumns}
              isEdit={isDataEdit}
              checkboxDisabled={!hasAccess('create', permissions)}
            />
            {actionLoading && (
              <div className="action-loader-overlay">
                <Loader />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
